import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { FaEye, FaCheckCircle } from "react-icons/fa";
import Cookies from "js-cookie";
import { useSetPassword } from "../../utils/hooks/useSetPassword";
import { useToast } from "../../components/shared/ToastProvider";
import SupportSidebarSetPassword from "./SupportSidebarSetPassword";
import { FaCopyright } from "react-icons/fa";
import latestLogo from "../../assets/latestLogo.svg";
import { LuEyeClosed } from "react-icons/lu";
import { MdOutlineLock } from "react-icons/md";
import { useTranslation } from "react-i18next";

export default function SetPassword() {
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { addToast } = useToast();
  const { i18n, t } = useTranslation();

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const tokenFromURL = searchParams.get("token");

    if (tokenFromURL) {
      Cookies.set("reset_token", tokenFromURL, { expires: 0.02 }); // ~30 minutes
      const cleanURL = location.pathname;
      window.history.replaceState({}, "", cleanURL);
    }
  }, [location]);

  const { mutate: setPasswordMutate, isPending } = useSetPassword();

  const isPasswordValid = () => {
    return (
      password.length >= 8 &&
      /\d/.test(password) &&
      /[^A-Za-z0-9]/.test(password)
    );
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const token = Cookies.get("reset_token");

    if (!token) {
      addToast(t("setPassword.toast.tokenMissing"), "error");
      return;
    }

    if (password === confirmPassword && isPasswordValid()) {
      setPasswordMutate(
        { password, token },
        {
          onSuccess: () => {
            addToast(t("setPassword.toast.success"), "success");
            Cookies.remove("reset_token");
            setTimeout(() => {
              navigate("/login");
            }, 1500);
          },
          onError: (error) => {
            addToast(
              error?.response?.data?.message ||
                t("setPassword.toast.linkInvalid"),
              "error",
            );
          },
        },
      );
    } else {
      addToast(t("setPassword.toast.passwordsMismatch"), "error");
    }
  };

  return (
    <div className="h-screen flex bg-[#F6FBFF] ">
      <div className="hidden md:flex flex-col  mt-[20px]">
        <SupportSidebarSetPassword />
      </div>
      <div className="flex-1 flex flex-col">
        <div className="w-full px-4 flex items-center justify-end">
          <div className="flex items-center m-4">
            <select
              className="h-9 text-base bg-[#F6FBFF] rounded px-3 py-1 ring-1 ring-green-500 focus:outline-none"
              value={i18n?.language}
              onChange={(e) => i18n.changeLanguage(e.target.value)}
            >
              <option value="en" className="text-base">
                English
              </option>
              <option value="da" className="text-base">
                Dansk
              </option>
            </select>
          </div>
        </div>
        <div className="flex-1 flex items-center justify-center px-4 overflow-hidden">
          <div className="w-full max-w-lg flex flex-col justify-center gap-2">
            <div className="text-center">
              <div className="font-sans p-1 rounded-sm flex justify-center items-center">
                <img
                  src={latestLogo}
                  alt="AssetEye Logo"
                  className="w-12 h-12"
                />
              </div>

              <h1 className="text-xl font-bold text-black mb-1 tracking-tight">
                {t("setPassword.titlePrefix")}{" "}
                <span className="text-[#3D6C50]">
                  {t("setPassword.titleSuffix")}
                </span>
              </h1>
              <p className="text-gray-600 mb-2 text-xs">
                {t("setPassword.description")}
              </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4 px-6">
              {/* Password Field */}
              <div>
                <label className="block text-sm font-sm text-gray-700 mb-2">
                  {t("setPassword.newPassword")}
                </label>
                <div className="relative">
                  <MdOutlineLock className="absolute left-0 ml-3 mt-2.5 items-center text-gray-400" />
                  <input
                    type="text"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="w-full bg-[#F1F6F9] text-sm pl-12 py-2 border border-gray-300 rounded-lg focus:outline-none bg-[#F6FBFF] focus:border-[#2E6243] pr-12 focus:shadow-[0_0_14px_rgba(0,0,0,0.17)] placeholder:text-gray-400 placeholder:opacity-100"
                    placeholder={t("setPassword.placeholderNew")}
                    required
                    autoComplete="new-password"
                    style={{
                      WebkitTextSecurity: showPassword ? "none" : "disc",
                    }}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700"
                  >
                    {showPassword ? <FaEye /> : <LuEyeClosed />}
                  </button>
                </div>
                {password && !isPasswordValid() && (
                  <p className="text-xs text-red-600 mt-1">
                    {t("setPassword.validationMessage")}
                  </p>
                )}
              </div>

              {/* Confirm Password Field */}
              <div>
                <label className="block text-sm font-sm text-gray-700 mb-2">
                  {t("setPassword.confirmPassword")}
                </label>
                <div className="relative">
                  <MdOutlineLock className="absolute left-0 ml-3 mt-2.5 items-center text-gray-400" />
                  <input
                    type="text"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="w-full bg-[#F1F6F9] text-sm pl-12 py-2 border border-gray-300 rounded-lg focus:outline-none bg-[#F6FBFF] focus:border-[#2E6243] pr-12 focus:shadow-[0_0_14px_rgba(0,0,0,0.17)] placeholder:text-gray-400 placeholder:opacity-100"
                    placeholder={t("setPassword.placeholderConfirm")}
                    required
                    autoComplete="new-password"
                    style={{
                      WebkitTextSecurity: showConfirmPassword ? "none" : "disc",
                    }}
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700"
                  >
                    {showConfirmPassword ? <FaEye /> : <LuEyeClosed />}
                  </button>
                </div>

                {confirmPassword && (
                  <div className="mt-2">
                    {password === confirmPassword ? (
                      <p className="text-xs text-green-600 flex items-center">
                        <FaCheckCircle className="w-3 h-3 mr-1" />
                        {t("setPassword.passwordsMatch")}
                      </p>
                    ) : (
                      <p className="text-xs text-red-600">
                        {t("setPassword.passwordsNotMatch")}
                      </p>
                    )}
                  </div>
                )}
              </div>

              <button
                type="submit"
                disabled={
                  !password ||
                  !confirmPassword ||
                  password !== confirmPassword ||
                  !isPasswordValid() ||
                  isPending
                }
                className="w-full text-white py-2 rounded-lg font-medium disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
                style={{ backgroundColor: "#3D6C50" }}
                onMouseEnter={(e) => {
                  if (!e.target.disabled) {
                    e.target.style.backgroundColor = "#3D6C50";
                  }
                }}
                onMouseLeave={(e) => {
                  if (!e.target.disabled) {
                    e.target.style.backgroundColor = "#4A7D5E";
                  }
                }}
              >
                {isPending
                  ? t("setPassword.submitting")
                  : t("setPassword.updateButton")}
              </button>
            </form>
          </div>
        </div>
        <div className="w-full flex justify-between">
          <a
            href="#"
            className="relative bottom-5 left-10 text-xs text-[#3D6C50] underline"
          >
            {t("setPassword.privacyPolicy")}
          </a>
          <span className="relative bottom-5 right-10 text-xs flex justify-center items-center text-gray-500">
            <FaCopyright />
            &nbsp; Copyright 2026
          </span>
        </div>
      </div>
    </div>
  );
}
